// ─── Public Status Page — operator-authored rich text ────────────────
//
// Incident bodies and incident updates are written by an operator in the PCP
// admin editor and stored as HTML. This page renders them to ANONYMOUS
// visitors, so the markup is treated as untrusted no matter who typed it: an
// account compromise, a bad paste out of Outlook, or an imported StatusCast
// body must not be able to run script, load a tracker, or break the layout.
//
// Two independent layers, because either one alone is a single point of failure:
//   1. The status host serves a strict `script-src 'self'` CSP — no inline
//      handlers, no third-party script. That is why public/status-theme.js is a
//      separate same-origin file rather than an inline <script> in status.html.
//   2. This ALLOWLIST sanitizer, below. Anything not explicitly permitted is
//      dropped — element, attribute and URL scheme alike.
//
// The sanitizer runs in an inert document (DOMParser), so nothing it inspects is
// ever connected to the live DOM: no <img src> is fetched, no <script> is
// evaluated, no custom element is upgraded while we walk it.

import { useMemo } from 'react';

/** Elements that survive. Everything else is either unwrapped or dropped whole. */
const ALLOWED_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 's', 'span', 'strong', 'sub', 'sup',
  'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
]);

/**
 * Elements dropped INCLUDING their subtree. Everything else that is merely not
 * allowed gets unwrapped (its text survives) — losing a <font> wrapper is fine,
 * silently rendering the contents of a <script> or a <style> is not.
 */
const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select',
  'textarea', 'link', 'meta', 'base', 'noscript', 'template', 'svg', 'math', 'audio', 'video',
]);

/** Per-tag attribute allowlist. Anything absent here is stripped — including
 *  every `on*` handler, `style`, `class` and `id` (no CSS injection, no id
 *  collision with the page's own anchors). */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
  ol: new Set(['start']),
};

const NUMERIC_ATTRS = new Set(['width', 'height', 'colspan', 'rowspan', 'start']);

/** Schemes a link may use. Relative URLs are allowed and resolve on our origin. */
const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];
/** Images may only come from http(s) — never `data:` (an SVG data URL is a
 *  script vector in some engines) and never `blob:`. */
const SAFE_IMG_SCHEMES = ['http:', 'https:'];

/** Control characters + whitespace, which is how `java\tscript:` and
 *  `java\nscript:` slip past a naive scheme check. */
function stripUrlNoise(value: string): string {
  let out = '';
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code > 0x20 && code !== 0x7f) out += ch;
  }
  return out;
}

function isSafeUrl(raw: string, schemes: string[]): boolean {
  const value = String(raw ?? '').trim();
  if (!value) return false;
  const cleaned = stripUrlNoise(value);
  if (!cleaned) return false;
  if (cleaned.startsWith('/') || cleaned.startsWith('#') || cleaned.startsWith('?')) return true;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(cleaned)) return true; // scheme-less relative path
  try {
    return schemes.includes(new URL(cleaned, 'https://status.invalid/').protocol);
  } catch {
    return false;
  }
}

/** Replace an element with its own children (keeps the text, loses the tag). */
function unwrap(el: Element): void {
  const parent = el.parentNode;
  if (!parent) {
    el.remove();
    return;
  }
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function sanitizeElement(el: Element): void {
  const tag = el.tagName.toLowerCase();

  if (DROP_WITH_CONTENT.has(tag)) {
    el.remove();
    return;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    // Walk the children FIRST so they are already clean when they get
    // re-parented onto this element's parent.
    for (const child of Array.from(el.children)) sanitizeElement(child);
    unwrap(el);
    return;
  }

  const allowed = ALLOWED_ATTRS[tag];
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (!allowed || !allowed.has(name)) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (NUMERIC_ATTRS.has(name)) {
      if (!/^\d{1,5}$/.test(attr.value.trim())) el.removeAttribute(attr.name);
      continue;
    }
    if (name === 'href' && !isSafeUrl(attr.value, SAFE_LINK_SCHEMES)) el.removeAttribute(attr.name);
    if (name === 'src' && !isSafeUrl(attr.value, SAFE_IMG_SCHEMES)) el.removeAttribute(attr.name);
  }

  if (tag === 'a') {
    if (!el.getAttribute('href')) {
      for (const child of Array.from(el.children)) sanitizeElement(child);
      unwrap(el); // an anchor with no usable href is just text
      return;
    }
    // Forced, never author-controlled: an operator's link opens in a new tab and
    // can never reach back into this window through `opener`.
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer nofollow ugc');
  }

  if (tag === 'img') {
    if (!el.getAttribute('src')) {
      el.remove();
      return;
    }
    el.setAttribute('loading', 'lazy');
    el.setAttribute('decoding', 'async');
    if (!el.getAttribute('alt')) el.setAttribute('alt', '');
  }

  for (const child of Array.from(el.children)) sanitizeElement(child);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Text-only projection — used for the incident search index and as the
 *  fallback when the parser itself fails. */
export function stripHtml(html: string | null | undefined): string {
  return String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Allowlist-sanitize an HTML fragment. Exported so a caller can pre-clean a
 *  body outside of React too. */
export function sanitizeStatusHtml(html: string): string {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    const body = doc.body;
    if (!body) return '';
    for (const child of Array.from(body.children)) sanitizeElement(child);
    return body.innerHTML;
  } catch {
    // A parser failure must never blank the page — degrade to escaped text.
    return escapeHtml(stripHtml(html));
  }
}

/** Plain text (an imported StatusCast body, or a hand-typed update) still has to
 *  read as prose: blank lines become paragraphs, single newlines become breaks. */
function plainTextToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para.trim()).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

export interface RichTextProps {
  html: string | null | undefined;
  className?: string;
}

export function RichText({ html, className }: RichTextProps) {
  const safe = useMemo(() => {
    const raw = String(html ?? '').trim();
    if (!raw) return '';
    // No tag at all ⇒ it is plain text, and rendering it verbatim would collapse
    // every line break. Wrap it in paragraphs instead.
    if (!/<[a-z!/]/i.test(raw)) return plainTextToHtml(raw);
    return sanitizeStatusHtml(raw);
  }, [html]);

  if (!safe) return null;
  return (
    <div
      className={className ? `sp-rich ${className}` : 'sp-rich'}
      // Safe by construction: `safe` is the output of the allowlist sanitizer
      // above, which drops every script, handler, style and unknown scheme.
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
